import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Cached from '@mui/icons-material/Cached';
import PropTypes from 'prop-types';
import EmailIcon from '@mui/icons-material/Email';
import {
  FETCH_TRACE,
  SEND_INVOICE_STATUS_MAIL,
  USER_INVOICE_PROFILE,
} from '../../graphql/invoices/invoice-profile';
import ChatDialog from '../chat/ChatDialog';
import traceToLabel from '../traces/traceText';
import { MoneyType } from '../../propTypes';
import DownloadInvoiceDocumentButton from '../buttons/DownloadInvoiceDocumentButton';
import dayjs from '../../config/dayjs';
import InvoiceStatusMailPreview from '../emailPreviews/InvoiceStatusPreview';
import CompanyContactList from '../lists/CompanyContactList';
import OperativeStepperDialog from '../dialogs/OperativeStepperDialog';
import {
  useBooleanState,
  useGetInvoice,
  useGetUser,
  useIsMobile,
} from '../../hooks';
import { openUrlAndDownload } from '../../helpers/open-url-and-download';
import {
  formatMoney,
} from '../../helpers/money';
import FingoSnackBar from '../snackBars/FingoSnackBar';
import TraceStatus from './TraceStatus';
import ProfileCard from './ProfileCard';
import ProfileTable from './ProfileTable';
import CessionTable from './CessionTable';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';

const stepToTitle = {
  0: 'Selecciona el tipo de correo',
  1: 'A quien quieres enviar',
  2: 'Confirmar',
};
const options = [{ value: 'unused', label: 'Notificar estado de la factura' }];

const getMostRecentEvent = (objects) => {
  const maxDate = objects.reduce(
    (prev, current) => (dayjs(prev.eventTime).isAfter(dayjs(current.eventTime)) ? prev : current),
  ).eventTime;
  return objects.filter((obj) => obj.eventTime === maxDate)[0];
};

const getLabelText = (invoice) => {
  if (invoice.traces.length === 0) {
    if (invoice.hasExecutiveMerit) {
      return 'Con mérito';
    }
    return `${invoice.daysToExecutiveMerit} días para el mérito`;
  }
  const event = getMostRecentEvent(invoice.traces);
  const label = traceToLabel(invoice, event.eventCode, event, true);
  if (typeof label.label === 'function') {
    return label.label();
  }
  return label.label;
};

const DefaultReferenceHeader = ({ amount, fromInvoice, toInvoice }) => {
  const invoice = fromInvoice || toInvoice;

  return (
    <Typography variant="body2">
      {invoice.dteType.name}{' '}
      <Link
        component={MuiLink}
        underline="hover"
        to={{
          pathname: '/app/invoice',
          search: `?invoiceId=${invoice.id}`,
        }}
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          '&:hover': {
            color: (theme) => `${theme.palette.primary.main} !important`,
          },
        }}
      >
        #
        {invoice.folio.length > 10
          ? `${invoice.folio.substring(0, 10)}...`
          : invoice.folio}
      </Link>
      <Typography variant="body2">${formatMoney(amount)}</Typography>
    </Typography>
  );
};

DefaultReferenceHeader.propTypes = {
  amount: MoneyType.isRequired,
  fromInvoice: PropTypes.shape({
    id: PropTypes.number,
    dteType: PropTypes.shape({
      name: PropTypes.string,
    }),
    folio: PropTypes.number,
  }),
  toInvoice: PropTypes.shape({
    id: PropTypes.number,
    dteType: PropTypes.shape({
      name: PropTypes.string,
    }),
    folio: PropTypes.number,
  }),
};

DefaultReferenceHeader.defaultProps = {
  fromInvoice: null,
  toInvoice: null,
};

const BaseInvoiceProfile = ({
  summaryHeaders,
  referencesHeaders: customReferencesHeaders,
  titleActions: customTitleActions,
  customCessionActions,
  customRelatedDocumentsActions,
  needsCreditNote,
  ReferenceHeaderComponent,
  enableEmailFeature,
  enableChatFeature,
  userInvoiceProfileQuery,
  fetchTraceMutation,
  sendInvoiceStatusMailMutation,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const invoiceId = searchParams.get('invoiceId');
  const invoiceData = useGetInvoice(invoiceId, userInvoiceProfileQuery);
  const currentUser = useGetUser();
  const blurred = useMemo(
    () => currentUser.requireChangePassword && currentUser.demo,
    [currentUser],
  );
  const [snackBarOpen, toggleSnackBar] = useBooleanState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [selectedContactsIds, setSelectedContactsIds] = useState([]);
  const [open, toggleOpen] = useBooleanState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const isMobile = useIsMobile();
  const direction = isMobile ? 'column' : 'row';
  const [chatOpen, toggleChat] = useBooleanState(Boolean(searchParams.get('chatOpen')));
  const [sendMail, { loading: loadingMail }] = useMutation(
    sendInvoiceStatusMailMutation,
    {
      variables: {
        invoiceId,
        contactsIds: selectedContactsIds,
        emailType: invoiceData && getLabelText(invoiceData),
        userId: currentUser.id,
      },
      onCompleted: () => {
        setSelectedContactsIds([]);
        setSelectedOption('');
        toggleOpen();
        setCurrentStep(0);
        setSnackBarMessage('Correo enviado con éxito');
        toggleSnackBar();
      },
    },
  );
  const [fetchTrace, { loading: loadingTraces }] = useMutation(fetchTraceMutation, {
    variables: {
      invoiceId: invoiceData?.id,
    },
    onError: () => {},
  });

  const referenceGetValue = useCallback(
    (reference) => <ReferenceHeaderComponent {...reference} />,
    [ReferenceHeaderComponent],
  );

  const referencesHeaders = customReferencesHeaders || [
    [
      {
        label: '',
        getValue: referenceGetValue,
      },
    ],
  ];

  const defaultTitleActions = [
    {
      id: 'invoice-export-xml',
      title: 'Descargar XML',
      icon: (
        <DownloadInvoiceDocumentButton
          invoice={invoiceData}
          showTitle
          sx={{ size: 'small' }}
          documentType="XML"
        />
      ),
      hide: !currentUser.selectedCompany.navBarTitles.has('my_purchases'),
    },
    {
      id: 'invoice-export-pdf',
      title: 'Descargar PDF',
      icon: (
        <DownloadInvoiceDocumentButton
          invoice={invoiceData}
          showTitle
          sx={{ size: 'small' }}
          documentType="PDF"
        />
      ),
    },
  ];

  const emailAction = enableEmailFeature ? [{
    id: 'send-mails',
    title: 'Enviar mail',
    icon: currentUser.selectedCompany.navBarTitles.has('my_purchases') && (
      <EmailIcon fontSize="medium" className={DEFAULT_ICON_CLASS_NAME} />
    ),
    onClick: () => {
      toggleOpen();
    },
    hide: !currentUser.selectedCompany.navBarTitles.has('my_purchases'),
  }] : [];

  const titleActions = [...defaultTitleActions, ...emailAction, ...(customTitleActions || [])];

  if (!invoiceData) return null;

  return (
    <>
      {enableEmailFeature && (
        <OperativeStepperDialog
          open={open}
          onClose={toggleOpen}
          firstStepOptions={options}
          setOptions={setSelectedOption}
          selectedOption={selectedOption}
          selectedContactsIds={selectedContactsIds}
          setSelectedContactsIds={setSelectedContactsIds}
          managersAndAttachs={[]}
          setManagersAndAttachs={() => {}}
          masterEntityTarget={invoiceData.receiver}
          SecondStepComponent={CompanyContactList}
          stepToTitle={stepToTitle}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          onSubmit={sendMail}
          loadingSubmit={loadingMail}
          lastStepComponent={(
            <InvoiceStatusMailPreview
              invoiceId={Number(invoiceData.id)}
              userId={Number(currentUser.id)}
              emailType={
                invoiceData?.traces ? getLabelText(invoiceData) : 'Proceso'
              }
            />
          )}
        />
      )}
      <FingoSnackBar
        open={snackBarOpen}
        setOpen={toggleSnackBar}
        message={snackBarMessage}
        severity="error"
      />
      {enableChatFeature && chatOpen && (
        <ChatDialog open={chatOpen} onClose={toggleChat} invoice={invoiceData} />
      )}
      <Typography component="span" variant="h3" color="text.highlight">
        {`${invoiceData.dteType.name} ${invoiceData.folio}`}
      </Typography>
      <Stack direction={direction} spacing={2} sx={{ paddingTop: '15px' }}>
        <Stack direction="column" spacing={2} flex={0.65}>
          <ProfileCard titleActions={titleActions}>
            <ProfileTable headersList={summaryHeaders} data={[invoiceData]} />
          </ProfileCard>
          <Stack direction={direction} spacing={2}>
            <Stack direction="column" spacing={2} flex={0.7}>
              <ProfileCard primaryTitle="Cesiones" blurred={blurred}>
                <CessionTable cessions={invoiceData.cessions} />
              </ProfileCard>
              {customCessionActions}
            </Stack>
            <Stack direction="column" spacing={2} flex={0.3}>
              <ProfileCard
                blurred={blurred}
                primaryTitle="Documentos Relacionados"
                customActions={(
                  <>
                    {invoiceData && needsCreditNote && needsCreditNote(invoiceData) && (
                      <Grid paddingX="40px">
                        <Typography variant="caption" color="text.primary">
                          <span role="img" aria-label="eyes">
                            👀
                          </span>{' '}
                          Recuerda <b>emitir la nota de crédito</b> asociada a
                          esta factura y evitar el cobro del iva
                        </Typography>
                      </Grid>
                    )}
                    {customRelatedDocumentsActions}
                  </>
                )}
              >
                <ProfileTable
                  data={[
                    ...invoiceData.intermediateReferencesFrom,
                    ...invoiceData.intermediateReferencesTo,
                  ]}
                  headersList={referencesHeaders}
                />
              </ProfileCard>
            </Stack>
          </Stack>
        </Stack>
        <Stack flex={0.35}>
          <ProfileCard
            primaryTitle="Traza"
            blurred={blurred}
            titleActions={[
              {
                id: 'invoice-refresh-trace',
                icon: <Cached fontSize="large" />,
                onClick: fetchTrace,
                loading: loadingTraces,
              },
            ]}
            top
            customActions={
              invoiceData.receiver.company?.instructions && (
                <CardActions>
                  <Tooltip
                    title={`Instructivo de ${invoiceData.receiver.name}`}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => openUrlAndDownload(
                        invoiceData.receiver.company.instructions,
                      )}
                      id="invoice-show-instructions"
                    >
                      ¿Problemas con la facturación?
                    </Button>
                  </Tooltip>
                </CardActions>
              )
            }
          >
            <TraceStatus invoice={invoiceData} />
          </ProfileCard>
        </Stack>
      </Stack>
    </>
  );
};

BaseInvoiceProfile.propTypes = {
  summaryHeaders: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        getValue: PropTypes.func,
        lightHeader: PropTypes.bool,
      }),
    ),
  ).isRequired,
  referencesHeaders: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        getValue: PropTypes.func,
      }),
    ),
  ),
  titleActions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.element,
      onClick: PropTypes.func,
      hide: PropTypes.bool,
    }),
  ),
  customCessionActions: PropTypes.node,
  customRelatedDocumentsActions: PropTypes.node,
  needsCreditNote: PropTypes.func,
  ReferenceHeaderComponent: PropTypes.elementType,
  enableEmailFeature: PropTypes.bool,
  enableChatFeature: PropTypes.bool,
  userInvoiceProfileQuery: PropTypes.shape({}),
  fetchTraceMutation: PropTypes.shape({}),
  sendInvoiceStatusMailMutation: PropTypes.shape({}),
};

BaseInvoiceProfile.defaultProps = {
  referencesHeaders: null,
  titleActions: [],
  customCessionActions: null,
  customRelatedDocumentsActions: null,
  needsCreditNote: null,
  ReferenceHeaderComponent: DefaultReferenceHeader,
  enableEmailFeature: true,
  enableChatFeature: true,
  userInvoiceProfileQuery: USER_INVOICE_PROFILE,
  fetchTraceMutation: FETCH_TRACE,
  sendInvoiceStatusMailMutation: SEND_INVOICE_STATUS_MAIL,
};

export default BaseInvoiceProfile;
